import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ClipboardText,
  MagnifyingGlass,
  CheckCircle,
  ArrowClockwise,
  Warning,
  DownloadSimple,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Topbar from "@/components/layout/Topbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import NumberInput from "@/components/ui/NumberInput";

/**
 * Inventario físico
 * -----------------
 * Muestra todos los productos con stock actual. El usuario ingresa los kilos
 * contados en cada uno. Luego "Validar" muestra las diferencias respecto al
 * stock del sistema y "Confirmar" aplica los ingresos/bajas.
 */
export default function InventarioPage() {
  const [filas, setFilas] = useState([]);
  // Cámaras (columnas) — persistidas en localStorage para que no se pierdan.
  // Cada cámara es un string con su nombre editable.
  const [camaras, setCamaras] = useState(() => {
    try {
      const raw = localStorage.getItem("inv_camaras");
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : ["Cámara 1"];
    } catch (_) { return ["Cámara 1"]; }
  });
  useEffect(() => {
    try { localStorage.setItem("inv_camaras", JSON.stringify(camaras)); } catch (_) {}
  }, [camaras]);

  // Conteos: { producto_key: { [camara_idx]: "12.5", ... } }
  const [conteos, setConteos] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [motivo, setMotivo] = useState("Recuento de inventario físico");
  const [preview, setPreview] = useState(null); // resultado del dry_run
  const [applying, setApplying] = useState(false);
  const [stockVisible, setStockVisible] = useState(false); // solo se revela al Validar
  const [askCompleto, setAskCompleto] = useState(false); // ¿es inventario completo?
  // Rubros: para permitir hacer inventario segmentado por rubro (por ejemplo
  // sólo "CARNE") y que la lógica de auto-cero por no-contado se limite a
  // esos rubros. Ahora es multi-select: el operador puede elegir varios
  // (ej. CARNE + POLLO) y contar ambos rubros en un mismo inventario.
  const [rubros, setRubros] = useState([]);
  const [rubrosSel, setRubrosSel] = useState([]); // [] = todos los rubros

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pl, ru] = await Promise.all([
        api.get("/stock/inventario/plantilla", {
          params: rubrosSel.length > 0 ? { rubros_ids: rubrosSel } : {},
          paramsSerializer: {
            indexes: null, // envía rubros_ids=a&rubros_ids=b (repetido)
          },
        }),
        api.get("/rubros").catch(() => ({ data: [] })),
      ]);
      setFilas(pl.data.items || []);
      setRubros(Array.isArray(ru.data) ? ru.data : []);
    } catch (e) {
      toast.error("No se pudo cargar la plantilla de inventario");
    } finally {
      setLoading(false);
    }
  }, [rubrosSel]);

  useEffect(() => {
    load();
  }, [load]);

  const keyOf = (f) => f.producto_id || f.codigo || f.producto_nombre;

  // Suma todos los conteos por cámara para un producto
  const sumaProd = (f) => {
    const row = conteos[keyOf(f)] || {};
    let total = 0;
    let hasAny = false;
    for (const idx in row) {
      const v = row[idx];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        total += parseFloat(v) || 0;
        hasAny = true;
      }
    }
    return { total, hasAny };
  };

  const filasFiltradas = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return filas;
    return filas.filter((f) => {
      return (
        (f.producto_nombre || "").toLowerCase().includes(query) ||
        (f.codigo || "").toLowerCase().includes(query)
      );
    });
  }, [q, filas]);

  const cargados = useMemo(
    () => filas.reduce((s, f) => s + (sumaProd(f).hasAny ? 1 : 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conteos, filas]
  );

  const setConteo = (f, camaraIdx, val) => {
    const k = keyOf(f);
    setConteos((prev) => ({
      ...prev,
      [k]: { ...(prev[k] || {}), [camaraIdx]: val },
    }));
  };

  const copiarActual = (f) => {
    const k = keyOf(f);
    // Poner el total en la primer cámara y limpiar las demás
    setConteos((prev) => ({
      ...prev,
      [k]: { 0: String(f.kilos_actuales ?? 0) },
    }));
  };

  const marcarCero = (f) => {
    const k = keyOf(f);
    setConteos((prev) => ({ ...prev, [k]: { 0: "0" } }));
  };

  const limpiar = () => {
    if (window.confirm("¿Limpiar todos los conteos ingresados?")) {
      setConteos({});
      setStockVisible(false);
    }
  };

  const agregarCamara = () => {
    setCamaras((prev) => [...prev, `Cámara ${prev.length + 1}`]);
  };

  const quitarCamara = (idx) => {
    if (camaras.length <= 1) {
      toast.error("Debe haber al menos una cámara");
      return;
    }
    if (!window.confirm(`¿Quitar la columna "${camaras[idx]}"?\nSe perderán los conteos ingresados en ella.`)) return;
    setCamaras((prev) => prev.filter((_, i) => i !== idx));
    // Recompactar los índices de conteos: quitar la columna idx y renumerar
    setConteos((prev) => {
      const next = {};
      for (const k in prev) {
        const row = {};
        for (const cIdx in prev[k]) {
          const n = parseInt(cIdx);
          if (n === idx) continue;
          row[n < idx ? n : n - 1] = prev[k][cIdx];
        }
        next[k] = row;
      }
      return next;
    });
  };

  const renombrarCamara = (idx, nombre) => {
    setCamaras((prev) => prev.map((c, i) => (i === idx ? nombre : c)));
  };

  const validar = () => {
    // Antes de aplicar, preguntamos si es inventario completo.
    // Chequeamos que haya al menos un conteo cargado.
    const hayConteo = filas.some((f) => sumaProd(f).hasAny);
    if (!hayConteo) {
      toast.error("Ingresá al menos un conteo antes de validar");
      return;
    }
    setAskCompleto(true);
  };

  const aplicar = async (esCompleto) => {
    setAskCompleto(false);
    const items = filas
      .filter((f) => sumaProd(f).hasAny)
      .map((f) => {
        const { total } = sumaProd(f);
        const row = conteos[keyOf(f)] || {};
        const desglose = camaras
          .map((nombre, idx) => {
            const v = row[idx];
            if (v === undefined || v === null || String(v).trim() === "") return null;
            return `${nombre}: ${parseFloat(v) || 0}`;
          })
          .filter(Boolean)
          .join(" · ");
        return {
          producto_id: f.producto_id || undefined,
          producto_nombre: f.producto_nombre,
          codigo: f.codigo || "",
          kilos_contados: total,
          observacion: desglose || undefined,
        };
      });

    if (items.length === 0) {
      toast.error("Ingresá al menos un conteo antes de validar");
      return;
    }
    try {
      const { data } = await api.post("/stock/inventario", {
        items,
        dry_run: false,
        es_completo: !!esCompleto,
        rubros_ids: rubrosSel.length > 0 ? rubrosSel : undefined,
        motivo_default: motivo || "Recuento de inventario físico",
      });
      setPreview(data);
      setStockVisible(true);
      const msgCompleto = data.bajas_por_completo > 0
        ? ` · ${data.bajas_por_completo} producto(s) sin contar → 0 kg`
        : "";
      toast.success(
        `Inventario aplicado · ${data.ingresos} ingreso(s) · ${data.bajas} baja(s)${msgCompleto}`
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudo aplicar el inventario"
      );
    }
  };

  const cerrarPreview = () => {
    // Al cerrar, refrescamos el stock desde el servidor (que ya está actualizado
    // por el validar) y limpiamos los conteos para volver al conteo a ciegas.
    setPreview(null);
    setConteos({});
    setStockVisible(false);
    load();
  };

  return (
    <>
      <Topbar />
      <div className="p-4 space-y-4">
        <Tabs defaultValue="conteo">
          <TabsList className="rounded-none border-b bg-neutral-50 h-11 px-2">
            <TabsTrigger value="conteo" data-testid="inv-tab-conteo">
              <ClipboardText size={14} className="mr-1" /> Conteo
            </TabsTrigger>
            <TabsTrigger value="historial" data-testid="inv-tab-historial">
              <ClockCounterClockwise size={14} className="mr-1" /> Historial
            </TabsTrigger>
          </TabsList>

          <TabsContent value="conteo" className="pt-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-neutral-800 flex items-center gap-2">
              <ClipboardText size={28} weight="fill" className="text-red-800" />
              Inventario físico
            </h1>
            <div className="text-[12px] text-neutral-500 mt-0.5">
              Contá cada producto, cargá los kilos y validá para ver las diferencias con el stock del sistema.
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="outline"
              onClick={load}
              className="h-9 gap-2"
              data-testid="inv-refresh"
            >
              <ArrowClockwise
                size={15}
                weight="bold"
                className={loading ? "animate-spin" : ""}
              />
              Actualizar
            </Button>
            <Button
              variant="outline"
              onClick={limpiar}
              className="h-9"
              disabled={cargados === 0}
              data-testid="inv-limpiar"
            >
              Limpiar
            </Button>
            <Button
              onClick={validar}
              className="h-9 gap-2 bg-[#921A0E] hover:bg-[#7d1509] text-white"
              disabled={cargados === 0}
              data-testid="inv-validar"
            >
              <CheckCircle size={15} weight="bold" /> Validar
            </Button>
          </div>
        </div>

        {/* Barra de búsqueda + rubro + motivo */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_1fr_auto] gap-2 items-center">
          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-500"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar producto o código…"
              className="pl-8 h-9"
              data-testid="inv-search"
            />
          </div>
          <RubrosMultiSelect
            rubros={rubros}
            selected={rubrosSel}
            onChange={(next) => {
              setRubrosSel(next);
              setConteos({});
              setPreview(null);
              setStockVisible(false);
            }}
          />
          <Input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivo (aplica a todos los ajustes)"
            className="h-9"
            data-testid="inv-motivo"
          />
          <Badge
            variant="outline"
            className="justify-center h-8 font-mono text-[11px] tracking-widest"
            data-testid="inv-cargados"
          >
            {cargados} / {filas.length} cargados
          </Badge>
        </div>

        {/* Cámaras (columnas de conteo) */}
        <div className="border border-[#E5E5E5] bg-neutral-50 rounded-sm p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase font-mono text-neutral-600 tracking-widest">
              Cámaras · Columnas de conteo
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={agregarCamara}
              className="h-7 text-[11px]"
              data-testid="inv-agregar-camara"
            >
              + Agregar cámara
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {camaras.map((nombre, idx) => (
              <div key={idx} className="flex items-center gap-1 bg-white border border-neutral-300 rounded px-1.5 py-1">
                <Input
                  value={nombre}
                  onChange={(e) => renombrarCamara(idx, e.target.value)}
                  className="h-7 w-32 text-[12px] border-0 focus-visible:ring-0 px-1"
                  data-testid={`inv-camara-nombre-${idx}`}
                />
                {camaras.length > 1 && (
                  <button
                    type="button"
                    onClick={() => quitarCamara(idx)}
                    className="text-neutral-400 hover:text-red-600 px-1"
                    title="Quitar cámara"
                    data-testid={`inv-camara-quitar-${idx}`}
                  >×</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tabla */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="grid gap-3 px-4 py-2 bg-neutral-100 text-[11px] uppercase font-mono text-neutral-600 border-b"
            style={{ gridTemplateColumns: `70px minmax(180px, 1fr) ${stockVisible ? "90px " : ""}repeat(${camaras.length}, 100px) 90px${stockVisible ? " 90px" : ""}` }}
          >
            <div>Código</div>
            <div>Producto</div>
            {stockVisible && <div className="text-right">Stock (kg)</div>}
            {camaras.map((nombre, idx) => (
              <div key={idx} className="text-right truncate" title={nombre}>{nombre}</div>
            ))}
            <div className="text-right">Total (kg)</div>
            {stockVisible && <div className="text-right">Diferencia</div>}
          </div>
          <div className="max-h-[62vh] overflow-y-auto">
            {filasFiltradas.map((f) => {
              const k = keyOf(f);
              const row = conteos[k] || {};
              const { total: contadoNum, hasAny: hasContado } = sumaProd(f);
              const delta = hasContado ? +(contadoNum - (f.kilos_actuales || 0)).toFixed(3) : null;
              return (
                <div
                  key={k}
                  className={`grid gap-3 px-4 py-1.5 border-b border-neutral-100 items-center text-sm ${
                    hasContado ? "bg-neutral-50" : ""
                  }`}
                  style={{ gridTemplateColumns: `70px minmax(180px, 1fr) ${stockVisible ? "90px " : ""}repeat(${camaras.length}, 100px) 90px${stockVisible ? " 90px" : ""}` }}
                  data-testid={`inv-row-${k}`}
                >
                  <div className="font-mono text-[11px] text-neutral-500">
                    {f.codigo || "—"}
                  </div>
                  <div className="truncate">{f.producto_nombre}</div>
                  {stockVisible && (
                    <div className="text-right font-mono">
                      {(f.kilos_actuales || 0).toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                    </div>
                  )}
                  {camaras.map((_, idx) => (
                    <NumberInput
                      key={idx}
                      step="0.001"
                      min="0"
                      value={row[idx] ?? ""}
                      onChange={(e) => setConteo(f, idx, e.target.value)}
                      placeholder="0.000"
                      className="h-8 text-right font-mono w-full"
                      data-testid={`inv-input-${k}-${idx}`}
                    />
                  ))}
                  <div className="text-right font-mono font-semibold text-[#921A0E]">
                    {hasContado ? contadoNum.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : "—"}
                  </div>
                  {stockVisible && (
                    <div className="text-right font-mono">
                      {delta === null ? (
                        <span className="text-neutral-300">—</span>
                      ) : delta === 0 ? (
                        <span className="text-neutral-400">0</span>
                      ) : delta > 0 ? (
                        <span className="text-emerald-700 font-bold">+{delta.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
                      ) : (
                        <span className="text-red-700 font-bold">{delta.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {filasFiltradas.length === 0 && (
              <div className="p-8 text-center text-neutral-400 text-sm">
                No hay productos que coincidan.
              </div>
            )}
          </div>
          {!stockVisible && (
            <div className="px-4 py-2 bg-amber-50/50 border-t border-amber-100 text-[11px] font-mono uppercase tracking-widest text-amber-800">
              Conteo a ciegas · el stock del sistema y las diferencias aparecen al presionar Validar
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="historial" className="pt-4">
            <HistorialInventarios />
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogo previo: ¿es inventario completo? */}
      <AlertDialog open={askCompleto} onOpenChange={setAskCompleto}>
        <AlertDialogContent data-testid="inv-completo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {rubrosSel.length > 0
                ? `¿Contaste ${rubrosSel.length === 1 ? "el rubro" : "los rubros"} "${rubros
                    .filter((r) => rubrosSel.includes(r.id))
                    .map((r) => r.nombre)
                    .join(", ")}" completo${rubrosSel.length === 1 ? "" : "s"}?`
                : "¿Es un inventario completo?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {rubrosSel.length > 0 ? (
                <>
                  Si <strong>sí</strong>: contaste TODOS los productos de{" "}
                  {rubrosSel.length === 1 ? "el rubro" : "los rubros"}{" "}
                  <strong>
                    {rubros
                      .filter((r) => rubrosSel.includes(r.id))
                      .map((r) => r.nombre)
                      .join(", ")}
                  </strong>
                  . Los productos de {rubrosSel.length === 1 ? "ese rubro" : "esos rubros"} que no cargaste pasarán a <strong>stock 0</strong>.
                  <br /><br />
                  Si <strong>no</strong>: sólo se aplican los ajustes de los productos que sí contaste dentro de{" "}
                  {rubrosSel.length === 1 ? "el rubro" : "los rubros"}; el resto (incluyendo productos de otros rubros) queda igual.
                </>
              ) : (
                <>
                  Si <strong>sí</strong>: contaste TODO el frigorífico y los productos que no
                  cargaste pasarán a <strong>stock 0</strong> (se dan de baja).
                  <br /><br />
                  Si <strong>no</strong>: sólo se aplicarán los ajustes de los productos que sí contaste;
                  el resto queda igual.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="inv-completo-cancelar">Cancelar</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => aplicar(false)}
              data-testid="inv-completo-no"
            >
              No, sólo los que conté
            </Button>
            <AlertDialogAction
              onClick={() => aplicar(true)}
              className="bg-[#921A0E] hover:bg-[#7d1509] text-white"
              data-testid="inv-completo-si"
            >
              {rubrosSel.length > 0
                ? `Sí, ${rubrosSel.length === 1 ? "rubro" : "rubros"} completo${rubrosSel.length === 1 ? "" : "s"}`
                : "Sí, inventario completo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialogo con el resultado del inventario ya aplicado */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && cerrarPreview()}>
        <DialogContent className="max-w-3xl" data-testid="inv-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-600" />
              Inventario aplicado
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="border border-neutral-200 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-neutral-500">Evaluados</div>
                  <div className="text-lg font-bold">{preview.total_evaluados}</div>
                </div>
                <div className="border border-emerald-200 bg-emerald-50 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-emerald-700">Ingresos</div>
                  <div className="text-lg font-bold text-emerald-700">{preview.ingresos}</div>
                </div>
                <div className="border border-red-200 bg-red-50 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-red-700">Bajas</div>
                  <div className="text-lg font-bold text-red-700">
                    {preview.bajas}
                    {preview.bajas_por_completo > 0 && (
                      <span className="text-[10px] font-normal ml-1">
                        (de las cuales {preview.bajas_por_completo} × sin contar)
                      </span>
                    )}
                  </div>
                </div>
                <div className="border border-neutral-200 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-neutral-500">Sin cambios</div>
                  <div className="text-lg font-bold text-neutral-600">{preview.sin_cambios}</div>
                </div>
              </div>

              <div className="max-h-72 overflow-y-auto border border-neutral-200 rounded">
                <table className="w-full text-[12px]">
                  <thead className="bg-neutral-50 sticky top-0 text-[10px] uppercase font-mono text-neutral-600">
                    <tr>
                      <th className="text-left px-2 py-1">Producto</th>
                      <th className="text-right px-2 py-1">Sistema</th>
                      <th className="text-right px-2 py-1">Contado</th>
                      <th className="text-right px-2 py-1">Δ</th>
                      <th className="text-left px-2 py-1">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.detalle_ingresos?.map((r, i) => (
                      <tr key={`i-${i}`} className="border-t border-neutral-100 bg-emerald-50/40">
                        <td className="px-2 py-1">{r.producto_nombre}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.kilos_actuales.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.kilos_contados.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        <td className="px-2 py-1 text-right font-mono text-emerald-700 font-bold">+{r.delta}</td>
                        <td className="px-2 py-1 text-neutral-500">{r.motivo}</td>
                      </tr>
                    ))}
                    {preview.detalle_bajas?.map((r, i) => (
                      <tr key={`b-${i}`} className="border-t border-neutral-100 bg-red-50/40">
                        <td className="px-2 py-1">{r.producto_nombre}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.kilos_actuales.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.kilos_contados.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        <td className="px-2 py-1 text-right font-mono text-red-700 font-bold">−{r.delta}</td>
                        <td className="px-2 py-1 text-neutral-500">{r.motivo}</td>
                      </tr>
                    ))}
                    {preview.procesados === 0 && (
                      <tr>
                        <td colSpan={5} className="p-4 text-center text-neutral-400">
                          <Warning size={18} className="inline mr-1" /> No hay diferencias: todo coincide con el stock del sistema.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            {preview?.inventario_id && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await api.get(`/stock/inventarios/${preview.inventario_id}/excel`, { responseType: "blob" });
                    const blob = new Blob([res.data], {
                      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `inventario_${preview.inventario_id.slice(0, 8)}.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (_) {
                    toast.error("No se pudo descargar el Excel");
                  }
                }}
                data-testid="inv-preview-excel"
              >
                Descargar Excel
              </Button>
            )}
            <Button
              onClick={cerrarPreview}
              className="bg-[#921A0E] hover:bg-[#7d1509] text-white"
              data-testid="inv-preview-cerrar"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -----------------------------------------------------------------------
 * Selector múltiple de rubros (Popover + Checkboxes)
 * ---------------------------------------------------------------------*/
function RubrosMultiSelect({ rubros, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const total = rubros.length;
  const seleccionados = selected.length;

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }
  };
  const seleccionarTodos = () => onChange([]); // "todos" = filtro vacío
  const nombres = rubros.filter((r) => selected.includes(r.id)).map((r) => r.nombre);

  let label = "Todos los rubros (inventario total)";
  if (seleccionados === 1) label = `Rubro: ${nombres[0]}`;
  else if (seleccionados > 1) label = `${seleccionados} rubros: ${nombres.join(", ")}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-9 rounded border border-neutral-300 bg-white px-2 text-sm text-left truncate hover:bg-neutral-50"
          data-testid="inv-rubros-select"
          title={label}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest px-2 pb-1">
          Rubros a inventariar
        </div>
        <button
          type="button"
          onClick={seleccionarTodos}
          className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-neutral-100 ${
            seleccionados === 0 ? "font-semibold text-[#921A0E]" : ""
          }`}
          data-testid="inv-rubros-todos"
        >
          {seleccionados === 0 ? "✓ " : ""}Todos los rubros (inventario total)
        </button>
        <div className="border-t border-neutral-100 my-1" />
        <div className="max-h-60 overflow-y-auto">
          {rubros.map((r) => {
            const checked = selected.includes(r.id);
            return (
              <label
                key={r.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-neutral-100 cursor-pointer text-sm"
                data-testid={`inv-rubro-opt-${r.id}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(r.id)}
                  data-testid={`inv-rubro-check-${r.id}`}
                />
                <span className="truncate">{r.nombre}</span>
              </label>
            );
          })}
          {total === 0 && (
            <div className="p-2 text-center text-neutral-400 text-[12px]">
              No hay rubros cargados.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* -----------------------------------------------------------------------
 * Historial de Inventarios físicos
 * ---------------------------------------------------------------------*/
function HistorialInventarios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stock/inventarios");
      setItems(Array.isArray(data) ? data : []);
    } catch (_) {
      toast.error("No se pudo cargar el historial de inventarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verDetalle = async (id) => {
    setDetalleLoading(true);
    setDetalle({ id, loading: true });
    try {
      const { data } = await api.get(`/stock/inventarios/${id}`);
      setDetalle(data);
    } catch (_) {
      toast.error("No se pudo cargar el detalle");
      setDetalle(null);
    } finally {
      setDetalleLoading(false);
    }
  };

  const descargarExcel = async (id) => {
    try {
      const res = await api.get(`/stock/inventarios/${id}/excel`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inventario_${id.slice(0, 8)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("No se pudo descargar el Excel");
    }
  };

  const fmtFecha = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (isNaN(d)) return s;
    return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-neutral-800">Historial de inventarios</h2>
          <div className="text-[12px] text-neutral-500">
            {items.length} inventario{items.length === 1 ? "" : "s"} registrado{items.length === 1 ? "" : "s"}
          </div>
        </div>
        <Button variant="outline" onClick={load} className="h-9 gap-2" data-testid="hist-refresh">
          <ArrowClockwise size={15} weight="bold" className={loading ? "animate-spin" : ""} />
          Actualizar
        </Button>
      </div>

      <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-neutral-100 text-[11px] uppercase font-mono text-neutral-600 border-b">
          <div className="col-span-2">Fecha</div>
          <div className="col-span-2">Usuario</div>
          <div className="col-span-2">Rubros</div>
          <div className="col-span-2">Motivo</div>
          <div className="col-span-1 text-right">Ing.</div>
          <div className="col-span-1 text-right">Bajas</div>
          <div className="col-span-2"></div>
        </div>
        <div className="max-h-[62vh] overflow-y-auto">
          {items.map((inv) => {
            const rubros = Array.isArray(inv.rubros_nombres) ? inv.rubros_nombres : [];
            return (
            <div
              key={inv.id}
              className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-neutral-100 items-center text-sm hover:bg-neutral-50"
              data-testid={`hist-row-${inv.id}`}
            >
              <div className="col-span-2 font-mono text-[12px]">{fmtFecha(inv.fecha)}</div>
              <div className="col-span-2 truncate">{inv.user_nombre || "—"}</div>
              <div className="col-span-2 truncate" title={rubros.join(", ") || "Todos los rubros"} data-testid={`hist-rubros-${inv.id}`}>
                {rubros.length === 0 ? (
                  <Badge variant="outline" className="text-[10px] font-mono">TODOS</Badge>
                ) : (
                  <span className="text-[11px] text-neutral-700">
                    {rubros.slice(0, 2).join(", ")}
                    {rubros.length > 2 && (
                      <span className="text-neutral-400"> +{rubros.length - 2}</span>
                    )}
                  </span>
                )}
              </div>
              <div className="col-span-2 truncate text-neutral-600">{inv.motivo || "—"}</div>
              <div className="col-span-1 text-right font-mono text-emerald-700 font-bold">
                +{inv.ingresos_count}
                <div className="text-[10px] text-emerald-600/70">{inv.kilos_ingresados} kg</div>
              </div>
              <div className="col-span-1 text-right font-mono text-red-700 font-bold">
                −{inv.bajas_count}
                <div className="text-[10px] text-red-600/70">{inv.kilos_dados_de_baja} kg</div>
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button
                  variant="ghost"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => verDetalle(inv.id)}
                  data-testid={`hist-ver-${inv.id}`}
                >
                  Ver
                </Button>
                <Button
                  variant="outline"
                  className="h-7 px-2 gap-1"
                  onClick={() => descargarExcel(inv.id)}
                  title="Descargar Excel"
                  data-testid={`hist-excel-${inv.id}`}
                >
                  <DownloadSimple size={12} />
                </Button>
              </div>
            </div>
          );})}
          {items.length === 0 && !loading && (
            <div className="p-8 text-center text-neutral-400 text-sm">
              Todavía no hay inventarios confirmados.
            </div>
          )}
        </div>
      </div>

      {/* Diálogo de detalle */}
      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-w-3xl" data-testid="hist-detalle-dialog">
          <DialogHeader>
            <DialogTitle>Detalle del inventario</DialogTitle>
          </DialogHeader>
          {detalleLoading || detalle?.loading ? (
            <div className="p-4 text-center text-neutral-500">Cargando…</div>
          ) : detalle && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="border border-neutral-200 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-neutral-500">Fecha</div>
                  <div className="text-[12px] font-bold">{fmtFecha(detalle.fecha)}</div>
                </div>
                <div className="border border-neutral-200 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-neutral-500">Usuario</div>
                  <div className="text-[12px] font-bold">{detalle.user_nombre || "—"}</div>
                </div>
                <div className="border border-emerald-200 bg-emerald-50 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-emerald-700">Ingresos</div>
                  <div className="text-lg font-bold text-emerald-700">+{detalle.kilos_ingresados} kg</div>
                </div>
                <div className="border border-red-200 bg-red-50 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-red-700">Bajas</div>
                  <div className="text-lg font-bold text-red-700">−{detalle.kilos_dados_de_baja} kg</div>
                </div>
              </div>
              <div className="text-[12px] text-neutral-500 italic">Motivo: {detalle.motivo || "—"}</div>
              <div className="text-[12px] text-neutral-600" data-testid="hist-detalle-rubros">
                <span className="uppercase font-mono text-[10px] text-neutral-500 mr-2">Rubros inventariados:</span>
                {Array.isArray(detalle.rubros_nombres) && detalle.rubros_nombres.length > 0 ? (
                  <span className="font-medium">
                    {detalle.rubros_nombres.join(", ")}
                    {detalle.es_completo && (
                      <Badge className="ml-2 bg-amber-600 text-white text-[9px]">RUBRO COMPLETO</Badge>
                    )}
                  </span>
                ) : (
                  <>
                    <Badge variant="outline" className="text-[10px] font-mono">TODOS LOS RUBROS</Badge>
                    {detalle.es_completo && (
                      <Badge className="ml-2 bg-amber-600 text-white text-[9px]">INVENTARIO COMPLETO</Badge>
                    )}
                  </>
                )}
              </div>
              <div className="max-h-[52vh] overflow-y-auto border border-neutral-200 rounded">
                <table className="w-full text-[12px]">
                  <thead className="bg-neutral-50 sticky top-0 text-[10px] uppercase font-mono text-neutral-600">
                    <tr>
                      <th className="text-left px-2 py-1 w-16">Tipo</th>
                      <th className="text-left px-2 py-1">Producto</th>
                      <th className="text-right px-2 py-1">Sistema</th>
                      <th className="text-right px-2 py-1">Contado</th>
                      <th className="text-right px-2 py-1">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.detalle_ingresos?.map((r, i) => (
                      <tr key={`i-${i}`} className="border-t border-neutral-100 bg-emerald-50/40">
                        <td className="px-2 py-1"><Badge className="bg-emerald-600 text-white text-[9px]">INGRESO</Badge></td>
                        <td className="px-2 py-1">{r.producto_nombre}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(r.kilos_actuales || 0).toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(r.kilos_contados || 0).toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        <td className="px-2 py-1 text-right font-mono text-emerald-700 font-bold">+{r.delta}</td>
                      </tr>
                    ))}
                    {detalle.detalle_bajas?.map((r, i) => (
                      <tr key={`b-${i}`} className="border-t border-neutral-100 bg-red-50/40">
                        <td className="px-2 py-1"><Badge className="bg-red-700 text-white text-[9px]">BAJA</Badge></td>
                        <td className="px-2 py-1">{r.producto_nombre}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(r.kilos_actuales || 0).toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(r.kilos_contados || 0).toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        <td className="px-2 py-1 text-right font-mono text-red-700 font-bold">−{r.delta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            {detalle && detalle.id && (
              <Button
                variant="outline"
                onClick={() => descargarExcel(detalle.id)}
                className="gap-1"
                data-testid="hist-detalle-excel"
              >
                <DownloadSimple size={14} /> Descargar Excel
              </Button>
            )}
            <Button onClick={() => setDetalle(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

